import React, { useState, useEffect } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { Header } from "../components/Header";
import { CheckCircle, Clock } from "lucide-react";
import { Button } from "../components/ui/button";
import alipayIcon from "../assets/images/zfb.png";

interface OrderInfo {
  projectName: string;
  projectNo: string;
  documentPrice: number;
  packages: string[];
  isConsortium: boolean;
  consortiumMembers: string;
  announcementId?: number;
}

export default function PaymentPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [orderInfo, setOrderInfo] = useState<OrderInfo | null>(null);
  const [paymentStatus, setPaymentStatus] = useState<"pending" | "processing" | "success">("pending");
  const [orderNo, setOrderNo] = useState<string>("");
  const [countdown, setCountdown] = useState(30 * 60); // 30分钟倒计时

  // 加载订单信息
  useEffect(() => {
    const orderId = searchParams.get("orderId");
    
    if (orderId) {
      // 从API获取订单信息
      fetchOrderInfo(orderId);
    } else {
      // 兼容旧的URL参数方式
      const packages = searchParams.get("packages");
      const consortium = searchParams.get("consortium");
      const consortiumMembers = searchParams.get("consortiumMembers");

      const mockOrder: OrderInfo = {
        projectName: "成都市某医院医疗设备采购项目",
        projectNo: "SCXY-2024-001",
        documentPrice: 500,
        packages: packages ? packages.split(",") : [],
        isConsortium: consortium === "1",
        consortiumMembers: consortiumMembers ? decodeURIComponent(consortiumMembers) : "",
      };

      setOrderInfo(mockOrder);
      const timestamp = Date.now();
      const random = Math.floor(Math.random() * 10000)
        .toString()
        .padStart(4, "0");
      setOrderNo(`ORD${timestamp}${random}`);
    }
  }, [searchParams]);

  // 获取订单信息
  const fetchOrderInfo = async (orderId: string) => {
    try {
      const token = localStorage.getItem("token");
      const resp = await fetch(`/api/trade-orders/${orderId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await resp.json();
      
      if (data.ok && data.data) {
        const order = data.data;
        setOrderNo(order.order_id);
        
        // 解析分包ID
        let packageIds: string[] = [];
        if (order.trade_announcement_package_id) {
          try {
            packageIds = JSON.parse(order.trade_announcement_package_id);
          } catch {
            packageIds = [];
          }
        }
        
        setOrderInfo({
          projectName: order.project_name || "",
          projectNo: order.project_no || "",
          documentPrice: order.order_amount || 0,
          packages: packageIds,
          isConsortium: !!order.union_member,
          consortiumMembers: order.union_member || "",
          announcementId: order.announcement_id,
        });
        
        // 如果已支付，直接显示成功页面
        if (order.payment_status === '已支付') {
          setPaymentStatus("success");
        }
      } else {
        // API返回错误，显示错误提示
        console.error('获取订单信息失败:', data.error);
        alert('获取订单信息失败：' + (data.error || '订单不存在'));
        navigate('/trade-announcements');
      }
    } catch (err) {
      console.error('获取订单信息失败:', err);
      alert('获取订单信息失败，请稍后重试');
      navigate('/trade-announcements');
    }
  };

  // 倒计时
  useEffect(() => {
    if (paymentStatus !== "pending") return;

    const timer = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(timer);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [paymentStatus]);

  // 格式化倒计时
  const formatCountdown = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, "0")}:${secs.toString().padStart(2, "0")}`;
  };

  // 处理支付宝支付
  const handleAlipayPayment = async () => {
    console.log('handleAlipayPayment 被调用', { orderInfo, orderNo });
    
    if (!orderInfo || !orderNo) {
      console.error('订单信息不完整:', { orderInfo, orderNo });
      alert("订单信息不完整，请刷新页面重试");
      return;
    }

    // 验证必需参数
    if (orderInfo.documentPrice === undefined || orderInfo.documentPrice === null) {
      console.error('订单金额信息不完整:', orderInfo.documentPrice);
      alert("订单金额信息不完整");
      return;
    }

    if (!orderInfo.projectName) {
      console.error('项目名称信息不完整:', orderInfo.projectName);
      alert("项目名称信息不完整");
      return;
    }

    console.log('开始处理支付，订单信息:', { 
      orderId: orderNo, 
      amount: orderInfo.documentPrice, 
      projectName: orderInfo.projectName 
    });
    
    setPaymentStatus("processing");

    try {
      // 获取token
      const token = localStorage.getItem("token");
      if (!token) {
        alert("请先登录");
        navigate("/my-bids");
        return;
      }

      // 调用后端创建支付订单（会更新订单状态为"支付中"）
      // 参数格式参照 payment/server/server.js: { orderNo, amount, message }
      const requestBody = {
        orderNo: orderNo,
        amount: orderInfo.documentPrice || 0,
        message: orderInfo.projectName || "",
      };
      
      console.log('发送支付请求:', requestBody);
      
      const response = await fetch("/api/payment/create", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(requestBody),
      });

      console.log('收到服务器响应:', response.status, response.statusText);
      
      const result = await response.json();
      console.log('服务器返回结果:', result);

      // 兼容两种返回格式：{ code: 0, data: payUrl } 和 { ok: true, paymentUrl: payUrl }
      if (result.code === 0 && result.data) {
        // payment 格式：{ code: 0, data: payUrl }
        console.log('准备跳转到支付宝支付页面:', result.data);
        window.location.href = result.data;
      } else if (result.ok && result.paymentUrl) {
        // 新格式：{ ok: true, paymentUrl: payUrl }
        console.log('准备跳转到支付宝支付页面:', result.paymentUrl);
        window.location.href = result.paymentUrl;
      } else if (result.ok && result.free) {
        // 免费订单直接完成
        console.log('免费订单，直接完成');
        setPaymentStatus("success");
      } else {
        console.error('服务器返回错误:', result);
        alert(result.error || result.message || "创建支付订单失败");
        setPaymentStatus("pending");
      }
    } catch (error) {
      console.error("支付错误:", error);
      alert("支付失败，请稍后重试: " + (error instanceof Error ? error.message : String(error)));
      setPaymentStatus("pending");
    }
  };


  // 格式化金额
  const formatMoney = (amount: number) => {
    return new Intl.NumberFormat("zh-CN", {
      style: "currency",
      currency: "CNY",
      minimumFractionDigits: 2,
    }).format(amount);
  };

  // 下载投标文件
  const handleDownloadFile = async () => {
    if (!orderNo) {
      alert('订单编号不存在');
      return;
    }

    try {
      const token = localStorage.getItem("token");
      if (!token) {
        alert("请先登录");
        navigate("/my-bids");
        return;
      }

      const resp = await fetch(`/api/trade-orders/${orderNo}/download-file`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      const data = await resp.json();
      
      if (data.ok && data.data && data.data.downloadUrl) {
        // 触发下载
        const link = document.createElement('a');
        link.href = data.data.downloadUrl;
        link.download = data.data.downloadUrl.split('/').pop() || '投标文件.pdf';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } else {
        alert(data.error || '未找到投标文件');
      }
    } catch (err) {
      console.error('下载文件失败:', err);
      alert('下载失败，请稍后重试');
    }
  };

  if (!orderInfo) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header simple />
        <div className="container mx-auto px-4 py-16 text-center">
          <p className="text-gray-500">加载订单信息中...</p>
        </div>
      </div>
    );
  }

  // 支付成功页面
  if (paymentStatus === "success") {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header simple />
        <div className="container mx-auto px-4 py-16">
          <div className="max-w-lg mx-auto bg-white rounded-xl shadow-lg p-8 text-center">
            <div className="w-20 h-20 mx-auto mb-6 bg-green-100 rounded-full flex items-center justify-center">
              <CheckCircle className="w-12 h-12" style={{ color: '#16a34a' }} />
            </div>
            <h2 className="text-2xl font-bold mb-2" style={{ color: '#16a34a' }}>支付成功</h2>
            <p className="text-gray-600 mb-6">您已成功报名参与该项目</p>

            <div className="bg-gray-50 rounded-lg p-4 mb-6 text-left">
              <div className="flex justify-between mb-2">
                <span className="text-gray-500">订单编号</span>
                <span className="text-gray-800 font-mono">{orderNo}</span>
              </div>
              <div className="flex justify-between mb-2">
                <span className="text-gray-500">支付金额</span>
                <span className="text-orange-500 font-bold">
                  {formatMoney(orderInfo.documentPrice)}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">项目名称</span>
                <span className="text-gray-800 text-right max-w-[200px] truncate">
                  {orderInfo.projectName}
                </span>
              </div>
            </div>

            <div className="mb-6 flex justify-center">
              <button
                onClick={handleDownloadFile}
                style={{
                  width: '40vw',
                  height: '36px',
                  padding: '0 24px',
                  backgroundColor: '#16a34a',
                  color: 'white',
                  fontSize: '14px',
                  fontWeight: 500,
                  borderRadius: '6px',
                  border: 'none',
                  cursor: 'pointer',
                  transition: 'background-color 0.2s',
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
                onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#15803d'}
                onMouseOut={(e) => e.currentTarget.style.backgroundColor = '#16a34a'}
              >
                下载招标文件
              </button>
            </div>

            <div className="flex gap-4">
              <Button
                variant="outline"
                className="flex-1"
                onClick={() => navigate("//my-bids")}
              >
                查看我的投标
              </Button>
              <Button
                className="flex-1 bg-[#003c8c] hover:bg-[#0050b8]"
                onClick={() => navigate("/trade-announcements")}
              >
                继续浏览公告
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header simple />

      <div className="container mx-auto px-4 py-8">
        <div className="max-w-3xl mx-auto">
          {/* 订单信息 */}
          <div className="bg-white rounded-xl shadow-lg overflow-hidden mb-6">
            <div className="px-6 py-4 bg-gradient-to-r from-[#003c8c] to-[#0050b8]">
              <h2 className="text-xl font-bold text-white">订单信息</h2>
            </div>

            <div className="p-6">
              {/* 订单倒计时 */}
              <div className="flex items-center justify-between mb-6 p-4 bg-orange-50 rounded-lg border border-orange-100">
                <div className="flex items-center gap-2 text-orange-600">
                  <Clock className="w-5 h-5" />
                  <span>请在 {formatCountdown(countdown)} 内完成支付</span>
                </div>
                <span className="text-2xl font-bold text-orange-500">
                  {formatMoney(orderInfo.documentPrice)}
                </span>
              </div>

              {/* 订单详情 */}
              <div className="space-y-4">
                <div className="flex justify-between py-3 border-b border-gray-100">
                  <span className="text-gray-500">订单编号</span>
                  <span className="text-gray-800 font-mono">{orderNo}</span>
                </div>
                <div className="flex justify-between py-3 border-b border-gray-100">
                  <span className="text-gray-500">项目编号</span>
                  <span className="text-gray-800 font-mono">{orderInfo.projectNo}</span>
                </div>
                <div className="flex justify-between py-3 border-b border-gray-100">
                  <span className="text-gray-500">项目名称</span>
                  <span className="text-gray-800 text-right max-w-[300px]">
                    {orderInfo.projectName}
                  </span>
                </div>
                <div className="flex justify-between py-3 border-b border-gray-100">
                  <span className="text-gray-500">采购文件售价</span>
                  <span className="text-orange-500 font-bold">
                    {formatMoney(orderInfo.documentPrice)}
                  </span>
                </div>
                {orderInfo.isConsortium && (
                  <div className="flex justify-between py-3 border-b border-gray-100">
                    <span className="text-gray-500">联合体成员</span>
                    <span className="text-gray-800">{orderInfo.consortiumMembers || "-"}</span>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* 支付方式 */}
          <div className="bg-white rounded-xl shadow-lg overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-100">
              <h3 className="text-lg font-bold text-gray-800">选择支付方式</h3>
            </div>

            <div className="p-6">
              {/* 支付宝支付 */}
              <div
                className="flex items-center justify-between p-4 border-2 border-[#1677FF] rounded-lg bg-blue-50/30 cursor-pointer"
                onClick={handleAlipayPayment}
              >
                <div className="flex items-center gap-3">
                  <img
                    src={alipayIcon}
                    alt="支付宝"
                    className="w-8 h-8 object-contain"
                  />
                  <div>
                    <p className="font-medium text-gray-800">支付宝支付</p>
                    <p className="text-sm text-gray-500">推荐使用支付宝付款</p>
                  </div>
                </div>
                <div className="w-5 h-5 rounded-full border-2 border-[#1677FF] flex items-center justify-center">
                  <div className="w-3 h-3 rounded-full bg-[#1677FF]" />
                </div>
              </div>

              {/* 支付按钮 */}
              <Button
                className="w-full mt-6 h-12 text-lg bg-[#1677FF] hover:bg-[#0958d9]"
                onClick={handleAlipayPayment}
                disabled={paymentStatus === "processing"}
              >
                {paymentStatus === "processing" ? (
                  <span className="flex items-center gap-2">
                    <svg
                      className="animate-spin h-5 w-5"
                      viewBox="0 0 24 24"
                      fill="none"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      />
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      />
                    </svg>
                    处理中...
                  </span>
                ) : (
                  `支付宝支付 ${formatMoney(orderInfo.documentPrice)}`
                )}
              </Button>

            </div>
          </div>

          {/* 温馨提示 */}
          <div className="mt-6 p-4 bg-yellow-50 rounded-lg border border-yellow-100">
            <h4 className="font-medium text-yellow-800 mb-2">温馨提示</h4>
            <ul className="text-sm text-yellow-700 space-y-1">
              <li>• 请在规定时间内完成支付，逾期订单将自动取消</li>
              <li>• 支付成功后，您可以在"我的投标"中查看报名信息</li>
              <li>• 如有问题请联系客服：028-62073616</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}

